import { reaction } from 'mobx';
import { Container, Service } from 'typedi';

import { AnnouncementService } from '../services/AnnouncementService';
import { AnnouncementListStore } from '../stores/AnnouncementListStore';
import { AnnouncementStatus, type IAnnouncementEntity } from '../types/announcement';
import { readTableSettings, writeTableSettings } from '../utils/tableSettings';
import { SessionController } from './SessionController';

@Service()
export class AnnouncementListController {
  private readonly _store = Container.get(AnnouncementListStore);
  private readonly _service = Container.get(AnnouncementService);
  private readonly _session = Container.get(SessionController);

  constructor() {
    // Page size and sort outlive the visit, as pfm keeps its table settings.
    reaction(
      () => ({ pageSize: this._store.pageSize, sort: this._store.sort }),
      settings => writeTableSettings(settings),
    );
  }

  /** A new visit of the list: fresh state with the kept table settings, then the announcements. */
  open = (): Promise<void> => {
    this._store.reset(readTableSettings());
    return this.load();
  };

  retry = (): Promise<void> => {
    this._store.setLoading();
    return this.load();
  };

  /** Withdraws the item whose confirmation was accepted. */
  confirmWithdraw = async (): Promise<void> => {
    const item = this._store.withdrawing;
    this._store.setWithdrawing(null);
    if (!item) return;
    this._store.setActionError(null);
    try {
      await this._service.withdraw(item.id);
      await this.load();
    } catch (e) {
      this.reportActionError(e);
    }
  };

  // Draft and scheduled are stored whole; a published one changes through "edit published" without notifying again.
  // A version card goes through its own APIs with the payload of its form: UpsertVersionCard keeps its publish time,
  // a published card has no audience, CTA or end date, and the card's stored Important travels unchanged.
  togglePin = async (a: IAnnouncementEntity): Promise<void> => {
    if (this._store.isPinning(a.id)) return;
    this._store.startPinning(a.id);
    this._store.setActionError(null);
    const pinned = !a.pinned;
    const published = a.status === AnnouncementStatus.ANNOUNCEMENT_STATUS_PUBLISHED;
    try {
      if (a.businessVersion !== undefined && !published) {
        await this._service.upsertVersionCard({
          businessVersion: a.businessVersion,
          localizations: a.localizations,
          pinned,
          important: a.important,
        });
      } else if (a.businessVersion !== undefined) {
        await this._service.editPublished({
          id: a.id,
          important: a.important,
          pinned,
          localizations: a.localizations,
          notifyAgain: false,
        });
      } else if (published) {
        await this._service.editPublished({
          id: a.id,
          important: a.important,
          pinned,
          localizations: a.localizations,
          audience: a.audience,
          endAt: a.endAt,
          notifyAgain: false,
        });
      } else {
        await this._service.updateDraft({
          id: a.id,
          type: a.type,
          localizations: a.localizations,
          important: a.important,
          pinned,
          audience: a.audience,
          publishAt: a.publishAt,
          endAt: a.endAt,
        });
      }
      await this.load();
    } catch (e) {
      this.reportActionError(e);
    } finally {
      this._store.finishPinning(a.id);
    }
  };

  private load = async (): Promise<void> => {
    try {
      this._store.setLoaded(await this._service.list());
    } catch (e) {
      const error = this._session.reportError(e);
      if (error) this._store.setFailed(error);
    }
  };

  private reportActionError = (e: unknown): void => {
    const error = this._session.reportError(e);
    if (error) this._store.setActionError(error);
  };
}
